// ═══════════════════════════════════════════════════════════════════
// CategoryRail (Apr28 v4)
// ═══════════════════════════════════════════════════════════════════
// Horizontal auto-sliding rail of products for a single category.
// Replaces the old "Trending This Season" mixed grid on the homepage.
//
// Behaviour:
//   - Auto-scrolls left every 5s, pauses on hover/touch
//   - Manual swipe/drag works via native overflow-scroll (no JS needed)
//   - Shows max 12 products (controlled by parent)
//   - Renders nothing if `products` array is empty (parent handles skip)
//   - Click "View All" → /shop?category=<slug>
//
// Performance:
//   - Uses transform-based scroll (smooth, GPU-accelerated)
//   - IntersectionObserver pauses auto-scroll when off-screen
//   - cldCard() optimized image URLs (~70% smaller payloads)
// ═══════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PremiumProductCard from '../shop/PremiumProductCard';

const AUTO_SCROLL_INTERVAL_MS = 5000;
const SCROLL_AMOUNT_PX = 280; // ~ one card width on desktop

const CategoryRail = ({ category, products, hi, isMobile, isSmall }) => {
  const scrollRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);

  // ─── IntersectionObserver: only auto-scroll when visible ─────────
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  // ─── Auto-scroll loop ────────────────────────────────────────────
  useEffect(() => {
    if (paused || !inView) return;
    const interval = setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      // Loop back to start when reaching the end
      const atEnd = el.scrollLeft + el.clientWidth + 10 >= el.scrollWidth;
      if (atEnd) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: SCROLL_AMOUNT_PX, behavior: 'smooth' });
      }
    }, AUTO_SCROLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [paused, inView]);

  // No render if no products (defensive — parent should also filter)
  if (!products || products.length === 0) return null;

  const catLabel = hi && category.nameHi ? category.nameHi : category.name;
  const slug = category.slug || category.name?.toLowerCase().replace(/\s+/g, '-');

  return (
    <section style={S.section}>
      {/* Section header */}
      <div style={S.header}>
        <div>
          <div style={S.eyebrow}>✦ {hi ? 'श्रेणी' : 'Category'} ✦</div>
          <h2 style={S.title}>
            {hi ? 'ट्रेंडिंग ' : 'Trending in '}
            <em style={S.titleEm}>{catLabel}</em>
          </h2>
        </div>
        <Link to={`/shop?category=${encodeURIComponent(slug)}`} style={S.viewAll}>
          {hi ? 'सभी देखें' : 'View All'} <span style={S.arrow}>→</span>
        </Link>
      </div>

      {/* Horizontal scrolling rail */}
      <div
        ref={scrollRef}
        style={{
          ...S.rail,
          padding: isMobile ? '6px 14px 18px' : '6px 32px 24px',
        }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setTimeout(() => setPaused(false), 2000)}
        className="gg-cat-rail"
      >
        {products.slice(0, 12).map(p => {
          // Fixed width per card so the rail snaps cleanly. Responsive
          // (small phone vs phone vs desktop). Each card maintains 4:5
          // image inside the width.
          const cardWidth = isSmall ? 160 : isMobile ? 180 : 230;
          return (
            <div key={p._id} style={{ flex: '0 0 auto', width: cardWidth, scrollSnapAlign: 'start' }}>
              <PremiumProductCard product={p} hi={hi} variant="standard" />
            </div>
          );
        })}
      </div>
    </section>
  );
};

// PremiumProductCard handles all card chrome (badges, hover, Quick Add,
// wishlist, etc.) — CategoryRail just provides the horizontal scroller.

// ─── Styles ──────────────────────────────────────────────────────────
const S = {
  section: { padding: '36px 0 12px' },

  header: {
    padding: '0 32px 18px',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  eyebrow: {
    color: '#c9a84c',
    fontSize: 11,
    letterSpacing: 3,
    fontWeight: 600,
    fontFamily: 'system-ui',
    marginBottom: 8,
    opacity: 0.85,
    textTransform: 'uppercase',
  },
  title: {
    margin: 0,
    color: '#e8e8f5',
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontSize: 'clamp(20px, 3.5vw, 28px)',
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: '-0.3px',
  },
  titleEm: {
    fontStyle: 'italic',
    background: 'linear-gradient(135deg, #c9a84c 0%, #f0d080 50%, #c9a84c 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  viewAll: {
    color: '#c9a84c',
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 1,
    textDecoration: 'none',
    fontFamily: 'system-ui',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: 20,
    transition: 'all 0.2s',
    whiteSpace: 'nowrap',
  },
  arrow: { transition: 'transform 0.2s' },

  rail: {
    display: 'flex',
    gap: 14,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollSnapType: 'x proximity',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(201,168,76,0.25) transparent',
  },

  // Card styles for the rail (chrome only — PremiumProductCard owns the
  // actual card visuals; we just keep scroll-snap behaviour here).
};

export default CategoryRail;
